// Dashboard download menu — captures whatever dashboard tab is
// currently rendered into PDF, PNG, or self-contained HTML. Pure
// client-side: we screenshot the DOM via `html-to-image`, then either
// save it as a raster (PNG), wrap it across pages in jsPDF, or
// serialise the live node with inlined computed styles for HTML.

import { useState, type RefObject } from 'react'
import {
  Button, CircularProgress, ListItemIcon, ListItemText, Menu, MenuItem,
  Tooltip,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { toPng, toBlob } from 'html-to-image'
import jsPDF from 'jspdf'

import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined'

interface Props {
  /** Ref to the DOM node that holds the dashboard content. The button
   *  only ever screenshots its current children, so wiring this is
   *  enough — we don't need to know which tab is active. */
  targetRef: RefObject<HTMLElement | null>
  /** Filename stem. Extension is appended per format. Defaults to
   *  `overview-<yyyy-mm-dd>`. */
  filenameStem?: string
  /** Optional disabled flag — useful while the inner data is still
   *  loading so we don't capture an empty skeleton. */
  disabled?: boolean
}

export function DashboardDownload({ targetRef, filenameStem, disabled }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [busy, setBusy] = useState<null | 'pdf' | 'png' | 'html'>(null)
  const theme = useTheme()

  const open = Boolean(anchor)
  const stem = filenameStem ?? `overview-${new Date().toISOString().slice(0, 10)}`

  // Background colour for the capture so transparent panels don't
  // export as black on light themes (or vice versa). Reads the
  // theme's paper colour so the saved file matches what the user
  // sees on screen.
  const bgColour = theme.palette.background.default
  const isDark = theme.palette.mode === 'dark'

  function close() { setAnchor(null) }

  function triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 5_000)
  }

  /** Common pre-capture knobs shared by PNG + PDF. `pixelRatio: 2`
   *  doubles raster fidelity at the cost of memory — fine for our
   *  dashboards which are mostly text + small charts. */
  function captureOptions() {
    return {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: bgColour,
      // Skip the download button itself so it doesn't appear in the
      // captured image (rare but visually weird otherwise).
      filter: (node: HTMLElement) => !node.dataset?.exclude,
    }
  }

  async function exportPng() {
    if (!targetRef.current) return
    setBusy('png'); close()
    try {
      const blob = await toBlob(targetRef.current, captureOptions())
      if (blob) triggerDownload(blob, `${stem}.png`)
    } catch (e) {
      console.error('[dashboard-download] png failed:', e)
    } finally { setBusy(null) }
  }

  async function exportPdf() {
    if (!targetRef.current) return
    setBusy('pdf'); close()
    try {
      // Capture once as PNG, then slice across A4 pages so long
      // dashboards don't get crammed into one tiny page.
      const dataUrl = await toPng(targetRef.current, captureOptions())
      const img = new Image()
      img.src = dataUrl
      await new Promise<void>((res) => { img.onload = () => res() })

      const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' })
      const pageWidth  = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      // Scale to fit width — preserves the dashboard's visual layout.
      const scaledHeight = (img.height * pageWidth) / img.width
      let y = 0
      // jsPDF lets us draw the same image at a negative Y offset to
      // "scroll" through it across pages. Simpler than slicing the
      // bitmap with canvas operations.
      while (y < scaledHeight) {
        pdf.addImage(dataUrl, 'PNG', 0, -y, pageWidth, scaledHeight)
        y += pageHeight
        if (y < scaledHeight) pdf.addPage()
      }
      // Footer: small caption with date + URL on the last page only.
      pdf.setFontSize(8)
      pdf.setTextColor(120)
      pdf.text(
        `Generated ${new Date().toLocaleString()} · ${window.location.href}`,
        12, pageHeight - 10,
      )
      pdf.save(`${stem}.pdf`)
    } catch (e) {
      console.error('[dashboard-download] pdf failed:', e)
    } finally { setBusy(null) }
  }

  async function exportHtml() {
    if (!targetRef.current) return
    setBusy('html'); close()
    try {
      const html = serializeWithInlineStyles(targetRef.current, {
        title: stem, isDark, bgColour,
      })
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
      triggerDownload(blob, `${stem}.html`)
    } catch (e) {
      console.error('[dashboard-download] html failed:', e)
    } finally { setBusy(null) }
  }

  return (
    <>
      <Tooltip title={disabled ? 'Wait for the dashboard to finish loading' : 'Download the current tab'} arrow>
        <span>
          <Button
            data-exclude="true"
            onClick={(e) => setAnchor(e.currentTarget)}
            disabled={disabled || busy !== null}
            variant="outlined"
            size="small"
            startIcon={busy
              ? <CircularProgress size={14} />
              : <DownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{ fontSize: 12.5, fontWeight: 700, textTransform: 'none',
              borderRadius: 99, px: 1.75 }}
          >
            {busy === 'pdf' ? 'Exporting PDF…'
              : busy === 'png' ? 'Exporting PNG…'
              : busy === 'html' ? 'Exporting HTML…'
              : 'Download'}
          </Button>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={close}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 200, mt: 0.5 } } }}
      >
        <MenuItem onClick={exportPdf}>
          <ListItemIcon>
            <PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: '#DC2626' }} />
          </ListItemIcon>
          <ListItemText
            primary="PDF document"
            secondary="Multi-page A4 for printing or sharing"
            primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: 11, color: 'text.disabled' }}
          />
        </MenuItem>
        <MenuItem onClick={exportPng}>
          <ListItemIcon>
            <ImageOutlinedIcon sx={{ fontSize: 18, color: '#7C3AED' }} />
          </ListItemIcon>
          <ListItemText
            primary="PNG image"
            secondary="Single tall image, retina"
            primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: 11, color: 'text.disabled' }}
          />
        </MenuItem>
        <MenuItem onClick={exportHtml}>
          <ListItemIcon>
            <CodeOutlinedIcon sx={{ fontSize: 18, color: '#0D9488' }} />
          </ListItemIcon>
          <ListItemText
            primary="HTML report"
            secondary="Static snapshot · selectable text · prints cleanly"
            primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: 11, color: 'text.disabled' }}
          />
        </MenuItem>
      </Menu>
    </>
  )
}

// ─── HTML serialiser ───────────────────────────────────────────────
//
// Produces a print-friendly static snapshot. Interactive controls
// (inputs, selects, buttons, tabs, toggle groups) are stripped or
// replaced with text representations of their current value — they
// wouldn't function in a standalone HTML file anyway and they look
// broken once their JavaScript handlers are gone.
//
// Computed styles are inlined for everything that survives. Pseudo-
// elements (::before / ::after) are captured by walking the original
// tree and injecting equivalent <span> markers into the clone.

function serializeWithInlineStyles(
  node: HTMLElement,
  opts: { title: string; isDark: boolean; bgColour: string },
): string {
  const clone = node.cloneNode(true) as HTMLElement

  // Pull the currently-applied filters off the live DOM before we
  // strip the interactive controls. Any descendant that wants to
  // surface state in the export tags itself with `data-filter-
  // summary='[{label,value},...]'` — keeps the snapshot path
  // decoupled from each component's filter shape.
  const filterChips = readFilterSummary(node)

  // Pair the cloned tree with the live tree so getComputedStyle reads
  // from rendered DOM, not the detached clone (which yields empty).
  const liveAll  = node.querySelectorAll<HTMLElement>('*')
  const cloneAll = clone.querySelectorAll<HTMLElement>('*')

  // Visual / layout properties only — dumping all 700+ computed
  // values bloats the file and slows the saved page to a crawl.
  const KEEP_STYLES = [
    'background-color', 'background-image', 'background-position',
    'background-repeat', 'background-size',
    'color', 'font-family', 'font-size', 'font-style', 'font-weight',
    'letter-spacing', 'line-height', 'text-align', 'text-decoration',
    'text-transform', 'white-space', 'word-break', 'overflow-wrap',
    'display', 'flex', 'flex-direction', 'flex-wrap', 'flex-grow', 'flex-shrink',
    'justify-content', 'align-items', 'align-content', 'gap',
    'grid-template-columns', 'grid-template-rows', 'grid-gap', 'grid-column',
    'grid-row', 'order',
    'position', 'top', 'right', 'bottom', 'left', 'z-index',
    'width', 'height', 'min-width', 'min-height', 'max-width', 'max-height',
    'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
    'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
    'border-radius', 'border-top-left-radius', 'border-top-right-radius',
    'border-bottom-left-radius', 'border-bottom-right-radius',
    'border-top', 'border-right', 'border-bottom', 'border-left',
    'box-shadow', 'opacity', 'overflow', 'overflow-x', 'overflow-y',
    'transform', 'fill', 'stroke', 'stroke-width', 'vertical-align',
    'aspect-ratio',
  ]

  function inlineStyles(liveEl: HTMLElement, cloneEl: HTMLElement) {
    const cs = getComputedStyle(liveEl)
    const parts: string[] = []
    for (const prop of KEEP_STYLES) {
      const v = cs.getPropertyValue(prop)
      if (v && v !== '0px' && v !== 'auto' && v !== 'none' && v !== 'normal') {
        parts.push(`${prop}: ${v}`)
      }
    }
    cloneEl.setAttribute('style', parts.join('; '))
  }

  // 1. Strip interactive widgets the saved file can't drive — replace
  //    each with a static text representation of its current value so
  //    the snapshot still tells the reader "the filter said X".
  function stripInteractive(cloneEl: HTMLElement) {
    // <input> — replace with span showing value
    if (cloneEl.tagName === 'INPUT') {
      const inp = cloneEl as HTMLInputElement
      const span = document.createElement('span')
      span.textContent = inp.value || inp.placeholder || ''
      span.setAttribute('style', 'display: inline-block;')
      cloneEl.replaceWith(span)
      return true
    }
    // <select> — span with selected option text
    if (cloneEl.tagName === 'SELECT') {
      const sel = cloneEl as HTMLSelectElement
      const span = document.createElement('span')
      span.textContent = sel.options[sel.selectedIndex]?.text ?? ''
      span.setAttribute('style', 'display: inline-block; font-weight: 600;')
      cloneEl.replaceWith(span)
      return true
    }
    // <textarea> — span with value
    if (cloneEl.tagName === 'TEXTAREA') {
      const ta = cloneEl as HTMLTextAreaElement
      const span = document.createElement('span')
      span.textContent = ta.value || ''
      cloneEl.replaceWith(span)
      return true
    }
    // MUI Select dropdown arrow + clear button — these are SVG icons
    // with no meaning in a static snapshot. Hide them.
    if (cloneEl.classList?.contains?.('MuiSelect-icon')
      || cloneEl.classList?.contains?.('MuiAutocomplete-popupIndicator')
      || cloneEl.classList?.contains?.('MuiAutocomplete-clearIndicator')) {
      cloneEl.remove()
      return true
    }
    // Strip whole button row (toolbar action buttons) — they can't
    // do anything in the snapshot. Keep button text inline for
    // tab labels though.
    if (cloneEl.tagName === 'BUTTON') {
      const role = cloneEl.getAttribute('role')
      // Tabs render as buttons with role="tab" — keep them so the
      // active tab label is visible. Other buttons get stripped.
      if (role !== 'tab') {
        cloneEl.remove()
        return true
      }
      // Tab — strip click handlers + keep text only
      const span = document.createElement('span')
      span.textContent = cloneEl.textContent ?? ''
      const isActive = cloneEl.getAttribute('aria-selected') === 'true'
      span.setAttribute('style',
        `display: inline-block; padding: 6px 12px; font-weight: 700;
         font-size: 13px;
         color: ${isActive ? '#0D9488' : (opts.isDark ? '#94a3b8' : '#64748b')};
         border-bottom: 2px solid ${isActive ? '#0D9488' : 'transparent'};
         margin-right: 16px;`)
      cloneEl.replaceWith(span)
      return true
    }
    return false
  }

  // Walk live + clone trees together. When we drop a node, the
  // matching cloneAll[i] is gone but we still need to keep walking
  // — track which clones survived with a parallel array.
  const survivors: Array<{ live: HTMLElement; clone: HTMLElement } | null> = []
  for (let i = 0; i < liveAll.length; i++) {
    const live = liveAll[i]
    const cl   = cloneAll[i]
    if (!live || !cl) { survivors.push(null); continue }
    // If clone has already been removed from the tree by an earlier
    // strip pass (e.g. its parent was a stripped button), skip.
    if (!cl.isConnected && !clone.contains(cl)) { survivors.push(null); continue }
    if (stripInteractive(cl)) { survivors.push(null); continue }
    survivors.push({ live, clone: cl })
  }

  // 2. Now inline computed styles onto everything that survived.
  inlineStyles(node, clone)
  for (const pair of survivors) {
    if (pair) inlineStyles(pair.live, pair.clone)
  }

  // 3. Wrap with a print-ready HTML shell.
  const ts = new Date().toLocaleString()
  const exportName = opts.title.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  const chipsRow = filterChips.length > 0 ? `
    <div class="dl-chips">
      ${filterChips.map((c) =>
        `<span class="dl-chip"><em>${escapeHtml(c.label)}</em>${escapeHtml(c.value)}</span>`
      ).join('')}
    </div>
  ` : ''
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escapeHtml(exportName)}</title>
<style>
  html, body {
    margin: 0; padding: 0;
    background: ${opts.bgColour};
    color: ${opts.isDark ? '#e6edf3' : '#0f172a'};
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    -webkit-font-smoothing: antialiased;
  }
  .dl-banner {
    background: ${opts.isDark ? '#161b22' : '#F8FAFC'};
    border-bottom: 1px solid ${opts.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'};
    padding: 20px 32px;
  }
  .dl-banner .dl-eyebrow {
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #0D9488;
    margin-bottom: 4px;
    font-family: 'IBM Plex Mono', ui-monospace, monospace;
  }
  .dl-banner .dl-title {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.02em;
    margin: 0;
  }
  .dl-banner .dl-meta {
    font-size: 12.5px;
    color: ${opts.isDark ? '#94a3b8' : '#64748b'};
    margin-top: 8px;
  }
  .dl-banner .dl-meta strong {
    color: ${opts.isDark ? '#e6edf3' : '#0f172a'};
    font-weight: 700;
  }
  .dl-banner .dl-notice {
    margin-top: 14px;
    padding: 10px 14px;
    background: ${opts.isDark ? 'rgba(245, 158, 11, 0.08)' : '#FEF3C7'};
    border: 1px solid ${opts.isDark ? 'rgba(245, 158, 11, 0.25)' : '#FCD34D'};
    border-radius: 8px;
    color: ${opts.isDark ? '#fbbf24' : '#92400E'};
    font-size: 11.5px;
    font-weight: 600;
  }
  .dl-chips {
    margin-top: 14px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .dl-chip {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 999px;
    background: ${opts.isDark ? 'rgba(13, 148, 136, 0.10)' : '#ECFEFF'};
    border: 1px solid ${opts.isDark ? 'rgba(13, 148, 136, 0.30)' : '#A5F3FC'};
    color: ${opts.isDark ? '#5EEAD4' : '#0F766E'};
    font-size: 12px;
    font-weight: 600;
  }
  .dl-chip em {
    font-style: normal;
    font-size: 9.5px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0.7;
  }
  .dl-body { padding: 24px 32px 48px; }
  @media print {
    .dl-banner .dl-notice { display: none; }
    body { background: white !important; color: black !important; }
  }
</style>
</head>
<body>
<div class="dl-banner">
  <div class="dl-eyebrow">// CMMS · Dashboard snapshot</div>
  <h1 class="dl-title">${escapeHtml(exportName)}</h1>
  <div class="dl-meta">Exported <strong>${escapeHtml(ts)}</strong> · ${escapeHtml(window.location.href)}</div>
  ${chipsRow}
  <div class="dl-notice">
    📄 Static snapshot — charts, dropdowns and filters are not interactive in this export.
    For live data, open the dashboard in the app.
  </div>
</div>
<div class="dl-body">${clone.outerHTML}</div>
</body>
</html>`
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!))
}

/**
 * Read every `[data-filter-summary]` blob inside the captured node
 * and flatten them into a single label/value list for the HTML
 * banner. Components opt in by JSON-stringifying their current
 * filter state into that attribute — the download path stays
 * decoupled from each component's filter shape, and tabs without
 * filters (General / My Overview) emit nothing and the chip row
 * just doesn't render.
 */
function readFilterSummary(liveNode: HTMLElement): Array<{ label: string; value: string }> {
  const collected: Array<{ label: string; value: string }> = []
  const nodes = liveNode.querySelectorAll<HTMLElement>('[data-filter-summary]')
  nodes.forEach((el) => {
    try {
      const raw = el.dataset?.filterSummary
      if (!raw) return
      const parsed = JSON.parse(raw) as unknown
      if (!Array.isArray(parsed)) return
      for (const entry of parsed) {
        if (entry && typeof entry === 'object'
            && typeof (entry as any).label === 'string'
            && typeof (entry as any).value === 'string') {
          collected.push({
            label: (entry as any).label,
            value: (entry as any).value,
          })
        }
      }
    } catch {
      // Malformed JSON — skip silently rather than tank the export.
    }
  })
  return collected
}
